import {Tab, Tabs} from "../../../../../components/base/Tabs";
import React, {useCallback, useEffect, useMemo} from "react";
import {Col, Row} from "reactstrap";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {Route, Switch, useHistory, useParams} from "react-router-dom";
import NCRActionGroupsSetting from "./action-groups";
import NCRNoteTitleSuggestionsSetting from "./note-title-suggestions";
import NCRSettingsConfigurationsView from "./configurations";
import NCRProcessesSetting from "./process-list";
import NCRAStatusesSetting from "./statuses";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

export const NCRSettingsTabs = {
    statuses: 'statuses',
    processList: 'process-List',
    actionsGroup: 'actions-group',
    noteTitleSuggestions: 'note-title-suggestions',
    configurations: 'configurations',
}

const NCRSettings = () => {
    const params = useParams()
    const history = useHistory()
    const {configureNotebookTags} = useConfigureNotebook('ncr-settings');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => params?.tab ?? NCRSettingsTabs.statuses, [params.tab]);
    const tabs = useMemo(() => Object.values(NCRSettingsTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case NCRSettingsTabs.statuses:
                    return 'Statuses';
                case NCRSettingsTabs.processList:
                    return 'Process List';
                case NCRSettingsTabs.actionsGroup:
                    return 'Actions Group';
                case NCRSettingsTabs.noteTitleSuggestions:
                    return 'Note Title Suggestions';
                case NCRSettingsTabs.configurations:
                    return 'Configurations';
                default:
                    return "";
            }
        })()
    })), []);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.ncrSettings(tab));
        configureBehavioralTool((tags) => tags.ncrSettings(tab));
    }, [configureBehavioralTool, configureNotebookTags, tab]);

    /**
     * Generate the path for the sub-routes of this view.
     * @type {function(*): string}
     */
    const generateRoute = useCallback((tab) => routeFunctions.ncr.settings(tab), []);

    /**
     * Changes the tab value of the params to the new value to reflect the change.
     * @type {(function(*, *): void)}
     */
    const onTabChanged = useCallback((event, newValue) => {
        history.push(generateRoute(newValue));
    }, [history, generateRoute]);

    /**
     * With each change in params:
     * - checks if the tab param has a correct value, and if not, changes the tab value to basic.
     */
    useEffect(() => {
        const tab = params?.tab;
        if (!tab || !tabs.map(e => e.value).includes(tab)) {
            return history.replace(generateRoute(NCRSettingsTabs.statuses));
        }
    }, [params, history, tabs, generateRoute]);


    return (
        <div className={'ncr-settings'}>
            <Row noGutters>
                <Col xs={12}>
                    <h1 className={'text-xl font-weight-semi-bold '}>
                        NCR Settings
                    </h1>
                </Col>
            </Row>
            <Tabs value={tab ?? false} onChange={onTabChanged}>
                {
                    tabs?.map((tab) => (
                        <Tab
                            key={tab?.value}
                            value={tab?.value}
                            label={tab?.name}
                        />
                    ))
                }
            </Tabs>
            <Col xs={12} className={'mb-3'}/>
            <Switch>
                <Route path={generateRoute(NCRSettingsTabs.statuses)} exact>
                    <NCRAStatusesSetting/>
                </Route>
                <Route path={generateRoute(NCRSettingsTabs.processList)} exact>
                    <NCRProcessesSetting/>
                </Route>
                <Route path={generateRoute(NCRSettingsTabs.actionsGroup)} exact>
                    <NCRActionGroupsSetting/>
                </Route>
                <Route path={generateRoute(NCRSettingsTabs.noteTitleSuggestions)} exact>
                    <NCRNoteTitleSuggestionsSetting/>
                </Route>
                <Route path={generateRoute(NCRSettingsTabs.configurations)} exact>
                    <NCRSettingsConfigurationsView/>
                </Route>
            </Switch>
        </div>
    )
}

export default NCRSettings;
